"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { CartItem } from "./cart";

const STORAGE_KEY = "edaga-cart";

type CartContextValue = {
  items: CartItem[];
  totalCount: number;
  totalCents: number;
  addItem: (item: CartItem) => void;
  updateQuantity: (tourStopId: string, tierId: string, quantity: number) => void;
  removeItem: (tourStopId: string, tierId: string) => void;
  clear: () => void;
};

const CartContext = createContext<CartContextValue | null>(null);

function loadStoredCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    // Corrupt or inaccessible localStorage — start with an empty cart.
    return [];
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setItems(loadStoredCart());
      setHydrated(true);
    }, 0);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items, hydrated]);

  const addItem = useCallback((item: CartItem) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.tourStopId === item.tourStopId && i.tierId === item.tierId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Math.min(item.maxQuantity, next[idx].quantity + item.quantity) };
        return next;
      }
      return [...prev, item];
    });
  }, []);

  const updateQuantity = useCallback((tourStopId: string, tierId: string, quantity: number) => {
    setItems((prev) => {
      if (quantity <= 0) return prev.filter((i) => !(i.tourStopId === tourStopId && i.tierId === tierId));
      return prev.map((i) => (i.tourStopId === tourStopId && i.tierId === tierId ? { ...i, quantity } : i));
    });
  }, []);

  const removeItem = useCallback((tourStopId: string, tierId: string) => {
    setItems((prev) => prev.filter((i) => !(i.tourStopId === tourStopId && i.tierId === tierId)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totalCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const totalCents = useMemo(() => items.reduce((sum, i) => sum + i.priceCents * i.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, totalCount, totalCents, addItem, updateQuantity, removeItem, clear }),
    [items, totalCount, totalCents, addItem, updateQuantity, removeItem, clear]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}
